import React, { useCallback, useState } from 'react';
import Box from '@mui/material/Box';

import { styles } from '../WorkingHours.styles';
import { TimeSlot, WorkingDay, TimeSlotEditorState } from '../WorkingHours.types';
import { getSlotBarLabel } from '../utils/slotLabelUtils';
import { KonnectAnalytics } from '../../../KonnectAnalytics/konnectAnalytics';
import TimeEditorPopover from './TimeEditorPopover';
import { WorkingHoursEditorValidation } from '../workingHoursSelectors';
import { getCarryInMinutesForDay } from '../utils/slotOverlapUtils';

type TimeSlotBarProps = {
  slot: TimeSlot;
  index: number;
  dayId: string;
  selectedSite: string;
  allSlots: TimeSlot[];
  isDraft: boolean;
  days: WorkingDay[];
  editor: TimeSlotEditorState;
  editorValidation: WorkingHoursEditorValidation | null;
  onUpdateSlot: (payload: {
    dayId: string;
    slotId: string;
    startMinutes: number;
    endMinutes: number;
  }) => void;
  onRemoveSlot: (payload: { dayId: string; slotId: string }) => void;
  // Distinct from onRemoveSlot (the bar's own ✕ button) - the popover's Delete button
  // needs its own path so the two can be told apart in analytics (see DayRow.tsx).
  onDeleteFromEditor: (slotId: string) => void;
  onOpenEditor: (payload: { dayId: string; slotId: string }) => void;
  onCloseEditor: () => void;
  onUpdateEditorDraft: (payload: {
    startMinutes?: number;
    endMinutes?: number;
    overnight?: boolean;
  }) => void;
  onSetEditorApplyDays: (payload: { applyDays: string[] }) => void;
  onApplyEditor: () => void;
  onEditStart: () => void;
  readOnly: boolean;
};

import { MINUTES_PER_DAY, SLOT_STEP_MINUTES } from '../workingHoursConstants';

const STEP_MINUTES = SLOT_STEP_MINUTES;

const snapToStep = (minutes: number) => Math.round(minutes / STEP_MINUTES) * STEP_MINUTES;

const clamp = (value: number, min: number, max: number) => Math.max(min, Math.min(max, value));

const TimeSlotBar = React.memo(({
  slot,
  index,
  dayId,
  selectedSite,
  allSlots,
  isDraft,
  days,
  editor,
  editorValidation,
  onUpdateSlot,
  onRemoveSlot,
  onDeleteFromEditor,
  onOpenEditor,
  onCloseEditor,
  onUpdateEditorDraft,
  onSetEditorApplyDays,
  onApplyEditor,
  onEditStart,
  readOnly,
}: TimeSlotBarProps) => {
  const [barEl, setBarEl] = useState<HTMLElement | null>(null);
  const normalizedEnd = slot.endMinutes <= slot.startMinutes ? MINUTES_PER_DAY : slot.endMinutes;
  const leftPercent = (slot.startMinutes / MINUTES_PER_DAY) * 100;
  const widthPercent = ((normalizedEnd - slot.startMinutes) / MINUTES_PER_DAY) * 100;

  const startDrag = useCallback(
    (handle: 'start' | 'end') => (e: React.MouseEvent) => {
      onEditStart();
      e.preventDefault();
      e.stopPropagation();

      const track = (e.currentTarget as HTMLElement).closest(
        '[data-timeline-track]'
      ) as HTMLElement;
      if (!track) return;

      const trackRect = track.getBoundingClientRect();

      // Compute adjacent slot bounds at drag start (order by startMinutes)
      const sorted = [...allSlots].sort((a, b) => a.startMinutes - b.startMinutes);
      const slotIndex = sorted.findIndex(s => s.id === slot.id);
      const prevSlotEnd =
        slotIndex > 0 ? sorted[slotIndex - 1].endMinutes : getCarryInMinutesForDay(days, dayId);
      const nextSlotStart =
        slotIndex < sorted.length - 1 ? sorted[slotIndex + 1].startMinutes : MINUTES_PER_DAY;

      let lastStart = slot.startMinutes;
      let lastEnd = slot.endMinutes;

      // Keep the popup's Start/End selects live while its own slot is being dragged —
      // but only for the exact day+slot the editor is currently open on.
      const isThisSlotBeingEdited = editor?.dayId === dayId && editor?.slotId === slot.id;

      const onMouseMove = (moveEvent: MouseEvent) => {
        const relativeX = moveEvent.clientX - trackRect.left;
        const rawMinutes = (relativeX / trackRect.width) * MINUTES_PER_DAY;
        const snapped = snapToStep(rawMinutes);

        if (handle === 'start') {
          const newStart = clamp(snapped, prevSlotEnd, normalizedEnd - STEP_MINUTES);
          lastStart = newStart;
          lastEnd = slot.endMinutes;
          onUpdateSlot({
            dayId,
            slotId: slot.id,
            startMinutes: newStart,
            endMinutes: slot.endMinutes,
          });
          if (isThisSlotBeingEdited) onUpdateEditorDraft({ startMinutes: newStart });
        } else {
          const newEnd = clamp(snapped, slot.startMinutes + STEP_MINUTES, nextSlotStart);
          lastStart = slot.startMinutes;
          lastEnd = newEnd;
          onUpdateSlot({
            dayId,
            slotId: slot.id,
            startMinutes: slot.startMinutes,
            endMinutes: newEnd,
          });
          if (isThisSlotBeingEdited) onUpdateEditorDraft({ endMinutes: newEnd });
        }
      };

      const onMouseUp = () => {
        KonnectAnalytics.workingHoursUpdateSlot({
          dayId,
          slotId: slot.id,
          startMinutes: lastStart,
          endMinutes: lastEnd,
          site: selectedSite,
        });
        window.removeEventListener('mousemove', onMouseMove);
        window.removeEventListener('mouseup', onMouseUp);
      };

      window.addEventListener('mousemove', onMouseMove);
      window.addEventListener('mouseup', onMouseUp);
    },
    [
      slot,
      normalizedEnd,
      allSlots,
      days,
      dayId,
      selectedSite,
      onUpdateSlot,
      onEditStart,
      editor,
      onUpdateEditorDraft,
    ]
  );

  const handleDelete = (e: React.MouseEvent) => {
    e.stopPropagation();
    onRemoveSlot({ dayId, slotId: slot.id });
  };

  const handleBarClick = (e: React.MouseEvent) => {
    if (readOnly) return;
    const target = e.target as HTMLElement;
    if (target.closest('.slot-handle') || target.closest('.slot-delete')) return;
    onOpenEditor({ dayId, slotId: slot.id });
  };

  const isEditorOpen = editor?.slotId === slot.id;
  const isOvernight = slot.endMinutes <= slot.startMinutes;

  return (
    <Box
      ref={setBarEl}
      className="slot-bar"
      onClick={handleBarClick}
      title={isDraft ? 'Unsaved change' : undefined}
      sx={{
        ...styles.slotBar,
        ...(isOvernight ? styles.slotBarOvernight : {}),
        ...(isDraft ? styles.slotBarDraft : {}),
        ...(readOnly ? { cursor: 'default' } : {}),
        left: `${leftPercent}%`,
        width: `${Math.max(widthPercent, 2)}%`,
      }}>
      {!readOnly && (
        <Box
          className="slot-handle"
          sx={{ ...styles.slotHandle, ...styles.slotHandleLeft }}
          onMouseDown={startDrag('start')}
        />
      )}
      <Box sx={styles.slotLabel}>
        {isOvernight && (
          <Box component="span" sx={styles.slotMoonIcon}>
            🌙
          </Box>
        )}
        {getSlotBarLabel(slot, index)}
      </Box>
      {!readOnly && (
        <>
          <Box
            className="slot-handle"
            sx={{ ...styles.slotHandle, ...styles.slotHandleRight }}
            onMouseDown={startDrag('end')}
          />
          <Box className="slot-delete" sx={styles.slotDeleteButton} title="Delete" onClick={handleDelete}>
            ✕
          </Box>
        </>
      )}

      {isEditorOpen && !readOnly && (
        <TimeEditorPopover
          anchorEl={barEl}
          editor={editor}
          validation={editorValidation}
          days={days}
          onUpdateDraft={onUpdateEditorDraft}
          onSetApplyDays={onSetEditorApplyDays}
          onApply={onApplyEditor}
          onCancel={onCloseEditor}
          onDelete={() => onDeleteFromEditor(slot.id)}
        />
      )}
    </Box>
  );
});

TimeSlotBar.displayName = 'TimeSlotBar';

export default TimeSlotBar;
